import type { Dispatch, SetStateAction } from 'react'
import { WorkersSelection, type WorkerSelectionItem } from '../../WorkersSelection'
import { SubActivitiesSection } from '../../SubActivitiesSection'
import { WorkAreaSection, type WorkAreaSelection } from '../../WorkAreaSection'
import { RiskAssessmentSection } from '../../RiskAssessmentSection'
import { RequiredDocumentsSection, type UploadedFileItem } from '../../RequiredDocumentsSection'
import type { TemplateSubactivity } from 'shared/api/templateService'

interface RequiredDocumentItem {
  id: string
  name: string
  code: string
  isMandatory: boolean
}

interface PermitAdditionalSectionsProps {
  tenantId: string
  isReadOnly: boolean
  canApproveCurrentStage?: boolean
  permitNo: string
  isSubactivityEnabled: boolean
  isWorkerEnabled: boolean
  isMapEnabled: boolean
  isRamEnabled: boolean
  isAttachmentEnabled: boolean
  selectedWorkers: WorkerSelectionItem[]
  setSelectedWorkers: (workers: WorkerSelectionItem[]) => void
  subActivities: TemplateSubactivity[]
  selectedActivities: string[]
  setSelectedActivities: (activities: string[]) => void
  extraFieldValues: Record<string, string>
  setExtraFieldValues: Dispatch<SetStateAction<Record<string, string>>>
  workAreaSelection: WorkAreaSelection
  setWorkAreaSelection: Dispatch<SetStateAction<WorkAreaSelection>>
  workLocationLabel?: string
  permitData: { workLocation: string; unit: string }
  riskData: any
  setRiskData: Dispatch<SetStateAction<any>>
  requiredDocuments: RequiredDocumentItem[]
  uploadedFiles: UploadedFileItem[]
  setUploadedFiles: Dispatch<SetStateAction<UploadedFileItem[]>>
  isDocumentsLoading?: boolean
  documentsError?: string | null
  getValidationContainerClass?: (key?: string | null) => string
}

/**
 * Groups together the worker/sub-activities/work-area/risk/documents sections so the main PermitRequestForm
 * file focuses on data wiring rather than JSX layout across multiple modules.
 */
export const PermitAdditionalSections = ({
  tenantId,
  isReadOnly,
  canApproveCurrentStage = false,
  permitNo,
  isSubactivityEnabled,
  isWorkerEnabled,
  isMapEnabled,
  isRamEnabled,
  isAttachmentEnabled,
  selectedWorkers,
  setSelectedWorkers,
  subActivities,
  selectedActivities,
  setSelectedActivities,
  extraFieldValues,
  setExtraFieldValues,
  workAreaSelection,
  setWorkAreaSelection,
  workLocationLabel,
  permitData,
  riskData,
  setRiskData,
  requiredDocuments,
  uploadedFiles,
  setUploadedFiles,
  isDocumentsLoading,
  documentsError,
  getValidationContainerClass,
}: PermitAdditionalSectionsProps) => {
  const isAdditionalSectionsDisabled = isReadOnly || canApproveCurrentStage

  return (
    <fieldset disabled={isAdditionalSectionsDisabled} className="space-y-6 border-0 p-0">
      {isSubactivityEnabled ? (
        <SubActivitiesSection
          subActivities={subActivities}
          selectedActivities={selectedActivities}
          setSelectedActivities={setSelectedActivities}
          extraFieldValues={extraFieldValues}
          setExtraFieldValues={setExtraFieldValues}
          getValidationContainerClass={getValidationContainerClass}
        />
      ) : null}
      {isWorkerEnabled ? (
        <WorkersSelection
          tenantId={tenantId}
          selectedWorkers={selectedWorkers}
          setSelectedWorkers={setSelectedWorkers}
        />
      ) : null}
      {isMapEnabled ? (
        <WorkAreaSection
          locationId={permitData.workLocation}
          unitId={permitData.unit}
          locationLabel={workLocationLabel}
          selection={workAreaSelection}
          setSelection={setWorkAreaSelection}
          isReadOnly={isAdditionalSectionsDisabled}
        />
      ) : null}
      {isRamEnabled ? (
        <RiskAssessmentSection
          riskData={riskData}
          setRiskData={setRiskData}
          getValidationContainerClass={getValidationContainerClass}
        />
      ) : null}
      {isAttachmentEnabled ? (
        <RequiredDocumentsSection
          requiredDocuments={requiredDocuments}
          uploadedFiles={uploadedFiles}
          setUploadedFiles={setUploadedFiles}
          tenantId={tenantId}
          permitNumber={permitNo}
          isLoading={isDocumentsLoading}
          errorMessage={documentsError}
          getValidationContainerClass={getValidationContainerClass}
        />
      ) : null}
    </fieldset>
  )
}
